import { IFsNodeSchema } from "@cloudike/web_files"
import { AlbumType, ShareAccessTypes, SharePermissionType } from "@cloudike/web_photos"
import { IAlbumSchema } from "@cloudike/web_photos/dist/types/intarfaces/IAlbumSchema"
import { ILinks } from "@cloudike/web_photos/dist/types/intarfaces/ILinks"
import { IMyShareSchema } from "@cloudike/web_photos/dist/types/intarfaces/IMyShareSchema"
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { boardsActions } from "features/boards/model/slice/boardsSlice"
import { NOTIFICATION_TYPES, showNotification } from "features/common/notifications"
import _ from "lodash"
import { RootState } from "providers/StoreProvider"
import { boardsApi } from "shared/api/boardsApi"
import { request } from "shared/api/request"
import { sharingApi } from "shared/api/sharingApi"
import { getFilesSdk } from "shared/sdk/files"
import { getPhotoSdk } from "shared/sdk/photo"
import { SDK_TYPES } from "shared/sdk/sdkConstants"
// import { hideGlobalProgressLoader, showGlobalProgressLoader } from "shared/ui/app-progress-bar"
// import { NOTIFICATION_TYPES, showNotification } from "shared/ui/notifications"
// import { t } from "i18next"
// import _ from "lodash"   
// import { getAlbumsSdkByType } from "sdk/albums"
// import { getFilesSdk } from "sdk/files"
// import { getPhotoSdk } from "sdk/photo"
// import { SDK_TYPES } from "sdk/sdkConstants"
// import { RootState } from "store"

// import { filesActions, setCurrentFolderThunk } from "../files/filesSlice"
// import APP_CONFIG from "../../constants/configs/app.config"
// import i18n from "../../i18n"
// import { appActions } from "../../store/app"
// import { getErrorData } from "../../utils/getErrorData"

export interface ICreateMyShareSchema {
  access_type?: ShareAccessTypes.ALL | ShareAccessTypes.PASSWORD | ShareAccessTypes.COLLABORATORS;
  password?: string;
  expires?: string;
  permission?: SharePermissionType.READ | SharePermissionType.WRITE;
}

export interface ICollaboratorSchema {
  created: string;
  updated: string;
  id: string;
  phone_or_email: string;
  permission: SharePermissionType.READ | SharePermissionType.WRITE;
  first_opened: string | null;
  _links?: ILinks;
}

export interface IExtendedMyShareSchema extends IMyShareSchema {
  permissions_groups: ISharingSettingsSchema[]
}

type Config = {
  sdkType: SDK_TYPES,
  sharedAlbumType?: AlbumType,
  isNewAlbum?: boolean,
  albumElementsCount?: number
} | null

export interface ISharingSettingsSchema {
  id: "upload_fs_nodes" | "download_fs_nodes" | "get_fs_node_versions" | "edit_fs_nodes" | "create_comments",
  enabled: boolean
}

interface SharingState {
  sharedItemData: IAlbumSchema & IFsNodeSchema,
  sharingData: IExtendedMyShareSchema | null
  config: Config,
  collaborators: ICollaboratorSchema[],
  availableTypesPublicLink: string[],
  sharingSettingsEnabled: ISharingSettingsSchema[],
  toggleSharingSettings: ISharingSettingsSchema[],
  isBusy: boolean
}

const initialState: SharingState = {
  sharedItemData: null,
  sharingData: null,
  config: null,
  collaborators: [],
  availableTypesPublicLink: [],
  sharingSettingsEnabled: [],
  toggleSharingSettings: [],
  isBusy: false
}

export const fetchShareDataThunk = createAsyncThunk(
  'sharing/fetchShareDataThunk',
  async function (__, { dispatch, getState }) {const state = getState() as RootState
    const sharedItemData = state.sharing.sharedItemData
    const sdkType = state.sharing.config.sdkType
    dispatch(actions.setIsBusy(true))

    if (sdkType === SDK_TYPES.FILES) {
      const filesSdk = getFilesSdk()
      const filesPublicLinksService = filesSdk.publicLinksService
      if (sharedItemData.is_shared) {
        let nodeData = sharedItemData

        if (!sharedItemData?._links?.share) {
          const { _links: { node } } = await boardsApi.getFsRoot(state.user.user?.family_user_id) as { _links: { node: { href: string } } }
          const urlTemplate = _.template(node.href, { interpolate: /\{(.+?)\}/g })
          const url = urlTemplate({ node_id: sharedItemData.id })

          nodeData = await request('GET', url, {}, { host: null })
        }

        const filesResponse = await filesPublicLinksService.getShare(nodeData as any)
        let collaborators = []

        if (filesResponse.data.collaborators_count) {
          try {
            const collaboratorsResponse = await filesPublicLinksService.getCollaborators(nodeData)

            collaborators = collaboratorsResponse.data._embedded.collaborators
          } catch (error) {
            collaborators = []
          }
        }

        dispatch(actions.setToggleSharingSettings((filesResponse.data as IMyShareSchema & { permissions_groups: ISharingSettingsSchema[] }).permissions_groups))
        dispatch(actions.setCollaborators(collaborators))
        dispatch(actions.setSharingData(filesResponse.data))
        dispatch(actions.setSharedItemData(nodeData))
        dispatch(actions.setIsBusy(false))

        const currentFolderId = state.files.currentFolderId

        if (currentFolderId === nodeData.id) {
          dispatch(boardsActions.setCurrentBoard(nodeData))
        }

        return
      }
      const { _links: { node } } = await boardsApi.getFsRoot(state.user.user?.family_user_id) as { _links: { node: { href: string } } }
      const urlTemplate = _.template(node.href, { interpolate: /\{(.+?)\}/g })
      const url = urlTemplate({ node_id: sharedItemData.id })

      const nodeData = await request('GET', url, {}, { host: null }) as IFsNodeSchema

      const filesResponse = await filesPublicLinksService.createShare(nodeData, { access_type: ShareAccessTypes.ALL })

      const nodeDataWithShareLink = await request('GET', url, {}, { host: null }) as IFsNodeSchema

      dispatch(actions.setSharingData(filesResponse.data))
      dispatch(actions.setSharedItemData({ ...nodeDataWithShareLink, is_shared: true }))

      dispatch(actions.setIsBusy(false))

      return
    }

    const publicLinksService = getPhotoSdk().publicLinksService

    if (sharedItemData.shared_hash) {
      const response = await publicLinksService.getShare(sharedItemData)

      let collaborators = []

      if (response.data.collaborators_count) {
        try {
          const collaboratorsResponse = await publicLinksService.getCollaborators(sharedItemData)

          collaborators = collaboratorsResponse.data._embedded.collaborators
        } catch (error) {
          collaborators = []
        }
      }

      dispatch(actions.setCollaborators(collaborators))
      dispatch(actions.setSharingData(response.data))
      dispatch(actions.setIsBusy(false))

      return
    }

    const response = await publicLinksService.createShare(sharedItemData, { access_type: ShareAccessTypes.ALL })

    dispatch(actions.setSharingData(response.data))
    dispatch(actions.setIsBusy(false))
  }
)

export const fetchCollaboratorsThunk = createAsyncThunk(
  'sharing/fetchCollaboratorsThunk',
  async function (_, { dispatch, getState }) {
    const state = getState() as RootState
    const userID = state.user.user?.family_user_id
    const nodeId = state.sharing.sharedItemData.id

    if (!userID || !nodeId) return

    try {
      const response = await sharingApi.getCollaborators(userID, nodeId)
      const collaborators = response?._embedded?.collaborators || []
      dispatch(actions.setCollaborators(collaborators))
    } catch (error) {
      console.error('Failed to fetch collaborators', error)
      dispatch(actions.setCollaborators([]))
    }
  }
)

export const addCollaboratorsThunk = createAsyncThunk(
  'sharing/addCollaboratorsThunk',
  async (emails: string[], { dispatch, getState }) => {
    dispatch(actions.setIsBusy(true))
    const state = getState() as RootState
    const { sharedItemData, config, toggleSharingSettings } = state.sharing
    const sdkType = config.sdkType

    const publicLinksService = sdkType === SDK_TYPES.FILES
      ? getFilesSdk().publicLinksService
      : getPhotoSdk().publicLinksService

    const successfulInvites: string[] = []
    const failedInvites: { email: string; error: any }[] = []

    const enabledPermissions = toggleSharingSettings.filter(setting => setting.enabled)
    const hasEnabledPermissions = enabledPermissions.length > 0

    try {
      if (state.sharing.sharingData && state.sharing.sharingData.access_type !== ShareAccessTypes.COLLABORATORS) {
        const response = await publicLinksService.updateShare(sharedItemData, { access_type: ShareAccessTypes.COLLABORATORS })
        dispatch(actions.setSharingData(response.data))
      }

      for (const email of emails) {
        try {
          const collaboratorData = { 
            phone_or_email: email,
            ...(hasEnabledPermissions && { permissions_groups: enabledPermissions })
          }
          await publicLinksService.addCollaborator(sharedItemData, collaboratorData)
          successfulInvites.push(email)
        } catch (error) {
          failedInvites.push({ email, error })
        }
      }

      if (failedInvites.length > 0) {
        showNotification({
          type: NOTIFICATION_TYPES.ERROR,
          message: "There was an error — please check the email address and try again.",
        })
        failedInvites.forEach(failure => {
          console.error(`Failed to add collaborator ${failure.email}:`, failure.error)
        })
      } else if (successfulInvites.length > 0) {
        showNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          message: "Your invite to access the shared content was sent successfully.",
        })
      }
      
      if (successfulInvites.length > 0) {
        dispatch(fetchCollaboratorsThunk())
      }

    } catch (error) {
      dispatch(actions.setIsBusy(false))
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Could not update sharing settings. Please try again.'
      })
    } finally {
      dispatch(actions.setIsBusy(false))
    }
  }
)

export const deleteCollaboratorThunk = createAsyncThunk(
  'sharing/deleteCollaboratorThunk',
  async function (collaboratorId: string, { dispatch, getState }) {
    const state = getState() as RootState
    const sharedItemData = state.sharing.sharedItemData
    const sdkType = state.sharing.config.sdkType

    if (!sharedItemData) return

    const publicLinksService = sdkType === SDK_TYPES.FILES ?
      getFilesSdk().publicLinksService :
      getPhotoSdk().publicLinksService

    try {
      const collaboratorToDelete = state.sharing.collaborators.find(c => c.id === collaboratorId)
      if (!collaboratorToDelete) {
        throw new Error("Collaborator not found")
      }

      await publicLinksService.deleteCollaborator(collaboratorToDelete)

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Collaborator removed',
        message: 'The user has been removed from the share.'
      })
      
      await dispatch(fetchCollaboratorsThunk())

      const updatedState = getState() as RootState
      const remainingCollaborators = updatedState.sharing.collaborators
      
      if (remainingCollaborators.length === 0 && 
          updatedState.sharing.sharingData?.access_type === ShareAccessTypes.COLLABORATORS) {
        await dispatch(updateSharingTypeThunk(ShareAccessTypes.ALL))
      }

    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Could not remove collaborator. Please try again.'
      })
    }
  }
)

export const updateSharingTypeThunk = createAsyncThunk(
  'sharing/updateSharingTypeThunk',
  async function (accessType: ShareAccessTypes.ALL | ShareAccessTypes.COLLABORATORS, { dispatch, getState }) {
    const state = getState() as RootState
    const sharedItemData = state.sharing.sharedItemData
    const sdkType = state.sharing.config.sdkType

    if (!sharedItemData) return

    const publicLinksService = sdkType === SDK_TYPES.FILES ?
      getFilesSdk().publicLinksService :
      getPhotoSdk().publicLinksService

    try {
      const response = await publicLinksService.updateShare(sharedItemData, { access_type: accessType })
      dispatch(actions.setSharingData(response.data))
      
      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Sharing type updated',
        message: accessType === ShareAccessTypes.ALL 
          ? 'Link is now accessible to everyone with the link'
          : 'Link is now restricted to collaborators only'
      })
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Could not update sharing type. Please try again.'
      })
    }
  }
)

// export const getPublicLinkAndCopyThunk = createAsyncThunk(
//   'sharing/getPublicLinkAndCopyThunk',
//   async function ({ data } : { data: any }, { getState, dispatch }) {
//     const state = getState() as RootState
//     const sharedItemData = data

//     const filesSdk = getFilesSdk()
//     const filesPublicLinksService = filesSdk.publicLinksService

//     if (sharedItemData.shared_hash) {
//       navigator.clipboard.writeText(`${window.location.origin}/links/pa/${sharedItemData.shared_hash}`)

//       showNotification({
//         type: NOTIFICATION_TYPES.SUCCESS,
//         title: i18n.t('l_notification_linkCopied'),
//         suffixText: i18n.t('a_notification_manageLink'),
//         callback: () => {
//           dispatch(actions.setSharingAlbumConfig({
//             sdkType: SDK_TYPES.DEFAULT,
//             sharedAlbumType: AlbumType.SHARED
//           }))

//           dispatch(actions.setSharedItemData(data))
//           dispatch(appActions.toggleRightMenuOnMobile(false))
//         },
//       })

//       return
//     }

//     if (sharedItemData.is_shared) {
//       let nodeData = sharedItemData

//       if (!sharedItemData?._links?.share) {
//         const { _links: { node } } = await filesApi.getFsRoot(state.user.userData.id)
//         const urlTemplate = _.template(node.href, { interpolate: /\{(.+?)\}/g })
//         const url = urlTemplate({ node_id: sharedItemData.id })

//         nodeData = await request('GET', url, {}, { host: null })
//       }

//       const filesResponse = await filesPublicLinksService.getShare(nodeData as any)

//       navigator.clipboard.writeText(filesResponse.data._links.public_url.href)

//       showNotification({
//         type: NOTIFICATION_TYPES.SUCCESS,
//         title: i18n.t('l_notification_linkCopied'),
//         suffixText: i18n.t('a_notification_manageLink'),
//         callback: () => {
//           dispatch(actions.setSharingAlbumConfig({
//             sdkType: SDK_TYPES.FILES
//           }))

//           dispatch(actions.setSharedItemData(nodeData))
//           dispatch(appActions.toggleRightMenuOnMobile(false))
//         },
//       })

//       return
//     }


//     const { _links: { node } } = await filesApi.getFsRoot(state.user.userData.id)
//     const urlTemplate = _.template(node.href, { interpolate: /\{(.+?)\}/g })
//     const url = urlTemplate({ node_id: sharedItemData.id })

//     const nodeData = await request('GET', url, {}, { host: null })


//     const filesResponse = await filesPublicLinksService.createShare(nodeData, { access_type: ShareAccessTypes.ALL })

//     navigator.clipboard.writeText(filesResponse.data._links.public_url.href)

//     showNotification({
//       type: NOTIFICATION_TYPES.SUCCESS,
//       title: i18n.t('l_notification_linkCopied'),
//       suffixText: i18n.t('a_notification_manageLink'),
//       callback: () => {
//         dispatch(actions.setSharingAlbumConfig({
//           sdkType: SDK_TYPES.FILES
//         }))

//         dispatch(actions.setSharedItemData(nodeData))
//         dispatch(appActions.toggleRightMenuOnMobile(false))
//       },
//     })

//     return
//   }
// )

// export const getPublicLinkAndCopyForPhotoThunk = createAsyncThunk(
//   'sharing/getPublicLinkAndCopyThunk',
//   async function ({ items, type, callback } : { items: any, type: SDK_TYPES, callback: () => void }, { dispatch }) {
//     const publicLinksService = getPhotoSdk().publicLinksService

//     const album = {
//       type: AlbumType.TMP,
//       description: ''
//     }

//     try {
//       showGlobalProgressLoader()

//       const albumSdk = getAlbumsSdkByType(type)

//       const response = await albumSdk.createAlbumWithItems(album, items)

//       const shareResponse = await publicLinksService.createShare(response.data, { access_type: ShareAccessTypes.ALL })

//       const link = shareResponse.data._links.public_url.href
//       navigator.clipboard.writeText(link)

//     } catch (error) {
//       showNotification({
//         type: NOTIFICATION_TYPES.WARNING,
//         ...getErrorData(error)
//       })
//     } finally {
//       showNotification({
//         type: NOTIFICATION_TYPES.SUCCESS,
//         title: i18n.t('l_notification_linkCopied'),
//         suffixText: i18n.t('a_notification_manageLink'),
//         callback: () => {
//           callback()
//           dispatch(appActions.toggleRightMenuOnMobile(false))
//         },
//       })
//       hideGlobalProgressLoader()
//     }
//   }
// )

export const deleteSharedLinkThunk = createAsyncThunk(
  'sharing/deleteSharedLinkThunk',
  async function ({ data, type, callback }: { data?: any, type?: SDK_TYPES, callback?: () => void }, { dispatch, getState }) {
    try {
      dispatch(actions.setIsBusy(true))
      const state = getState() as RootState
      const sharedItemData = data || state.sharing.sharedItemData
      const sdkType = type || state.sharing.config.sdkType
      const currentBoardId = state.boards.currentBoardId

      const publicLinksService = sdkType === SDK_TYPES.FILES ?
        getFilesSdk().publicLinksService :
        getPhotoSdk().publicLinksService

      await publicLinksService.deleteShare(sharedItemData)

      if (currentBoardId === sharedItemData.id) {
        dispatch(boardsActions.setCurrentBoard(sharedItemData))
      }

      dispatch(actions.resetState())
      dispatch(actions.setIsBusy(false))

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: "Delete link",
        message: "Link was deleted"
      })

      if (callback) {
        callback()
      }
    } catch (error) {
      if (!navigator.onLine) {
        return
      }

      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: "Delete link",
        message: 'Something went wrong'
      })
    } finally {

    }
  }
)

// export const updateSharedDataThunk = createAsyncThunk(
//   'sharing/updateSharedDataThunk',
//   async function ({ data, closeAfterUpdate }: { data: ICreateMyShareSchema, closeAfterUpdate?: boolean }, { dispatch, getState }) {
//     const state = getState() as RootState
//     const sharedItemData = state.sharing.sharedItemData
//     const sdkType = state.sharing.config.sdkType

//     const publicLinksService = sdkType === SDK_TYPES.FILES ?
//       getFilesSdk().publicLinksService :
//       getPhotoSdk().publicLinksService

//     const response = await publicLinksService.updateShare(sharedItemData, data as any)

//     if (!closeAfterUpdate) {
//       dispatch(actions.setSharingData(response.data))

//       return
//     }

//     dispatch(actions.resetState())

//     showNotification({
//       type: NOTIFICATION_TYPES.SUCCESS,
//       title: t('l_notification_changesSaved')
//     })
//   }
// )

export const saveSharedDataThunk = createAsyncThunk(
  'sharing/saveSharedDataThunk',
  async function ({ data, closeAfterUpdate }: { data: ICreateMyShareSchema, closeAfterUpdate?: boolean }, { dispatch, getState }) {
    dispatch(actions.setIsBusy(true))
    const state = (getState() as RootState).sharing
    const sharedItemData = state.sharedItemData
    const sdkType = state.config.sdkType

    const publicLinksService = sdkType === SDK_TYPES.FILES ?
      getFilesSdk().publicLinksService :
      getPhotoSdk().publicLinksService

    try {
      const response = await publicLinksService.updateShare(sharedItemData, data as any)
      if (!closeAfterUpdate) {
        dispatch(actions.setSharingData(response.data))
        return
      }
      dispatch(actions.resetState())
    } catch (error){
      
    } finally {
      dispatch(actions.setIsBusy(false))
    }
  }
)

// export const addCollaboratorsThunk = createAsyncThunk(
//   'sharing/addCollaboratorsThunk',
//   async function ({ collaborators, successCallback, errorCallback }: { collaborators: ICollaboratorSchema[], successCallback: () => void, errorCallback: (error: string) => void }, { dispatch, getState }) {
//     const state = (getState() as RootState).sharing
//     const sharedItemData = state.sharedItemData
//     const sharingData = state.sharingData
//     const currentCollaborators = state.collaborators
//     const sdkType = state.config.sdkType

//     const publicLinksService = sdkType === SDK_TYPES.FILES ?
//       getFilesSdk().publicLinksService :
//       getPhotoSdk().publicLinksService

//     try {
//       if (sharingData.access_type !== 'collaborators') {
//         const response = await publicLinksService.updateShare(sharedItemData, { access_type: 'collaborators' } as any)

//         dispatch(actions.setSharingData(response.data))
//       }

//       const response = await publicLinksService.addCollaborator(sharedItemData, collaborators[0] as any)

//       dispatch(actions.setCollaborators([...currentCollaborators, response.data]))

//       successCallback()
//     } catch (error) {
//       if (!navigator.onLine) {
//         return
//       }

//       if (error?.cause?.cause?.code === 'CollaboratorsCountExceededError') {
//         showNotification({
//           type: NOTIFICATION_TYPES.WARNING,
//           title: t('l_notification_usersListLimit')
//         })

//         return
//       }

//       if (error?.cause?.cause?.code === 'InvalidParameters') {
//         errorCallback(APP_CONFIG.user_login_type === 'email' ? t('l_notification_incorrectEmailError') : t('l_common_incorrectPhoneNumberError'))

//         return
//       }

//       showNotification({
//         type: NOTIFICATION_TYPES.WARNING,
//         title: t('l_common_wentWrong')
//       })
//     }
//   }
// )

// export const removeCollaboratorsThunk = createAsyncThunk(
//   'sharing/removeCollaboratorsThunk',
//   async function ({ phone_or_email }: { phone_or_email: ICollaboratorSchema['phone_or_email'] }, { dispatch, getState }) {
//     const state = (getState() as RootState).sharing
//     const collaborators = state.collaborators
//     const sdkType = state.config.sdkType

//     const publicLinksService = sdkType === SDK_TYPES.FILES ?
//       getFilesSdk().publicLinksService :
//       getPhotoSdk().publicLinksService

//     try {
//       const collaboratorIndex = collaborators.findIndex(collaborator => collaborator.phone_or_email === phone_or_email)
//       const collaboratorExistedInCollaborators = collaborators[collaboratorIndex]

//       await publicLinksService.deleteCollaborator(collaboratorExistedInCollaborators)

//       dispatch(actions.setCollaborators([...collaborators.slice(0, collaboratorIndex), ...collaborators.slice(collaboratorIndex + 1)]))
//     } catch (error) {
//       if (!navigator.onLine) {
//         return
//       }

//       showNotification({
//         type: NOTIFICATION_TYPES.WARNING,
//         title: t('l_common_wentWrong')
//       })
//     }
//   }
// )

// export const updateCollaboratorsThunk = createAsyncThunk(
//   'sharing/updateCollaboratorsThunk',
//   async function ({ phone_or_email, permission }: { phone_or_email: ICollaboratorSchema['phone_or_email'], permission: SharePermissionType }, { dispatch, getState }) {
//     const state = (getState() as RootState).sharing
//     const collaborators = state.collaborators
//     const sdkType = state.config.sdkType

//     const publicLinksService = sdkType === SDK_TYPES.FILES ?
//       getFilesSdk().publicLinksService :
//       getPhotoSdk().publicLinksService

//     try {
//       const collaboratorIndex = collaborators.findIndex(collaborator => collaborator.phone_or_email === phone_or_email)
//       const collaboratorExistedInCollaborators = collaborators[collaboratorIndex]

//       const response = await publicLinksService.updateCollaborator(collaboratorExistedInCollaborators, { permission })

//       dispatch(actions.setCollaborators([...collaborators.slice(0, collaboratorIndex), response.data, ...collaborators.slice(collaboratorIndex + 1)]))
//     } catch (error) {
//       if (!navigator.onLine) {
//         return
//       }

//       showNotification({
//         type: NOTIFICATION_TYPES.WARNING,
//         title: t('l_common_wentWrong')
//       })
//     }
//   }
// )

export const addAvailableTypesPublicLinkThunk = createAsyncThunk(
  'sharing/addAvailableTypesPublicLinkThunk',
  async function (_, { dispatch }) {

    const userSettingsService = getPhotoSdk().userSettingsService
    
    const userSharingData = await userSettingsService.getValue('user_sharing')
    const accessTypes = userSharingData.data['access_types']
    if (!!accessTypes) dispatch(actions.setAvailableTypesPublicLink([...accessTypes]))
  }
)


export const getSharingSettingsThunk = createAsyncThunk(
  'sharing/getSharingSettingsThunk',
  async function (nodeId: string, { dispatch, getState }) {
    dispatch(actions.setIsBusy(true))
    const state = getState() as RootState
    try {
      const settingsEnabled = await sharingApi.getSharingSettingsEnabled(state.user.user?.family_user_id, nodeId)

      dispatch(actions.setSharingSettingsEnabled(settingsEnabled.groups.map((group: any) => ({
        id: group.id,
        enabled: !!group.permissions_if_enabled.length
      }))))

    } catch (error) {
      console.log('error', error)
    } finally {
      dispatch(actions.setIsBusy(false))
    }
  }
)

export const patchSharingSettingsThunk = createAsyncThunk(
  'sharing/patchSharingSettingsThunk',
  async function ({ nodeId, permission }: {nodeId: string, permission: {id: string, enabled: boolean}}, { dispatch, getState }) {
    dispatch(actions.setIsBusy(true))
    const state = getState() as RootState

    const data = {
      permissions_groups: state.sharing.toggleSharingSettings.some(setting => setting.id === permission.id)
        ? state.sharing.toggleSharingSettings.map(setting => 
          setting.id === permission.id 
            ? { ...setting, enabled: permission.enabled }
            : setting
        )
        : [...state.sharing.toggleSharingSettings, { id: permission.id, enabled: permission.enabled }]
    }

    try {
      const response = await sharingApi.updateSharingSettings(state.user.user?.family_user_id, nodeId, data)
      dispatch(actions.setToggleSharingSettings(response.permissions_groups))

      if (state.sharing.collaborators.length > 0) {
        await dispatch(updateCollaboratorsPermissionsThunk(response.permissions_groups))
      }

    } catch (error) {
      console.log('error', error)
    } finally {
      dispatch(actions.setIsBusy(false))
    }
  }
)

export const updateCollaboratorsPermissionsThunk = createAsyncThunk(
  'sharing/updateCollaboratorsPermissionsThunk',
  async function (permissionsGroups: ISharingSettingsSchema[], { getState }) {
    const state = getState() as RootState
    const sharedItemData = state.sharing.sharedItemData
    const sdkType = state.sharing.config.sdkType
    const collaborators = state.sharing.collaborators

    if (!sharedItemData || collaborators.length === 0) return

    const publicLinksService = sdkType === SDK_TYPES.FILES ?
      getFilesSdk().publicLinksService :
      getPhotoSdk().publicLinksService

    try {
      await Promise.all(collaborators.map(async (collaborator) => {
        try {
          await publicLinksService.updateCollaborator(collaborator, { permissions_groups: permissionsGroups } as any)
        } catch (error) {
          console.error(`Failed to update permissions for collaborator ${collaborator.phone_or_email}:`, error)
        }
      }))

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Permissions updated',
        message: `Permissions updated for ${collaborators.length} collaborator${collaborators.length > 1 ? 's' : ''}`
      })

    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Could not update collaborator permissions. Please try again.'
      })
    }
  }
)

export const patchExpirationDateSharingSettingsThunk = createAsyncThunk(
  'sharing/patchExpirationDateSharingSettingsThunk',
  async function ({ nodeId, time }: {nodeId: string, time: string}, { dispatch, getState }) {
    dispatch(actions.setIsBusy(true))
    const state = getState() as RootState

    const data = {
      expires: time
    }

    try {
      await sharingApi.updateSharingSettings(state.user.user?.family_user_id, nodeId, data)
      dispatch(actions.setSharingData({ ...state.sharing.sharingData, expires: time }))

    } catch (error) {
      console.log('error', error)
    } finally {
      dispatch(actions.setIsBusy(false))
    }
  }
)

export const sharingSlice = createSlice({
  name: 'sharing',
  initialState,
  reducers: {
    setSharingData: (state, action) => {
      state.sharingData = action.payload
    },
    setSharedItemData: (state, action) => {
      state.sharedItemData = action.payload
    },
    setSharingAlbumConfig: (state, action: PayloadAction<Config>) => {
      state.config = action.payload
    },
    setCollaborators: (state, action) => {
      state.collaborators = action.payload
    },
    setAvailableTypesPublicLink: (state, action) => {
      state.availableTypesPublicLink = action.payload
    },
    setSharingSettingsEnabled: (state, action) => {
      state.sharingSettingsEnabled = action.payload
    },
    setToggleSharingSettings: (state, action) => {
      state.toggleSharingSettings = action.payload
    },
    resetState: (state) => {
      state.sharedItemData = null
      state.sharingData = null
      state.config = null
      state.collaborators = []
      state.sharingSettingsEnabled = []
      state.toggleSharingSettings = []
    },
    resetSharingSettings: (state) => {
      state.sharingSettingsEnabled = []
      state.toggleSharingSettings = []
    },
    setIsBusy: (state, action) => {
      state.isBusy = action.payload
    }
  }
})

const {
  reducer, actions
} = sharingSlice

export { actions as sharingActions, reducer as sharingReducer }

